import { useEffect, useEffectEvent, useRef, useState } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarHalfIcon from '@mui/icons-material/StarHalf';
import { useQuery } from '@tanstack/react-query';

import type { Sx } from '@/shared/types/uiTypes';
import { Box, Divider, IconButton, Typography } from '@/shared/ui';

import { captureEvent } from '@/services/analytics';

import { enqueueSnackbar } from 'notistack';
import Markdown from '@/features/_shared/Markdown';
import { useUserDetails } from '@/features/_shared/user';
import { feedArticleOptions } from '@/features/markets/components/Feed/components/feedArticle.options';
import { FeedArticleDialogSkeleton } from '@/features/markets/components/Feed/components/FeedArticleDialogSkeleton';
import type { MonthlyRecapsArticle } from '@/features/monthly-recaps';
import { clock } from '@/services/clock';

type MonthlyRecapArticleDialogProps = {
  article: MonthlyRecapsArticle;
  onBack: () => void;
  onClose: () => void;
};

const headerSx: Sx = {
  display: 'flex',
  alignItems: 'center',
  height: 69,
  flexShrink: 0,
  backgroundColor: 'gx.mainBackground',
  borderBottom: '1px solid rgba(248, 248, 248, 0.2)',
  position: 'sticky',
  top: 0,
  zIndex: 1
};

const iconButtonSx: Sx = {
  width: 48,
  height: 69,
  borderRadius: 0,
  flexShrink: 0,
  backgroundColor: 'gx.tileDark',
  '&:hover': { backgroundColor: 'gx.tileDark' }
};

const dotSx: Sx = {
  width: 4,
  height: 4,
  borderRadius: '50%',
  backgroundColor: 'gx.subtext',
  flexShrink: 0
};

const Dot = () => (
  <Box
    aria-hidden
    sx={dotSx}
  />
);

const getStarValue = (e: React.MouseEvent<HTMLButtonElement>, star: number): number => {
  const rect = e.currentTarget.getBoundingClientRect();
  return e.clientX - rect.left < rect.width / 2 ? star - 0.5 : star;
};

const starIconSx = { fontSize: 24, color: 'gx.highlight' };
const starEmptySx = { fontSize: 24, color: 'gx.text' };

const StarDisplay = ({ value, star }: { value: number; star: number }) => {
  if (value >= star) return <StarIcon sx={starIconSx} />;
  if (value >= star - 0.5) return <StarHalfIcon sx={starIconSx} />;
  return <StarBorderIcon sx={starEmptySx} />;
};

export const MonthlyRecapArticleDialog = ({
  article,
  onBack,
  onClose
}: MonthlyRecapArticleDialogProps) => {
  const { userEmail } = useUserDetails();
  const [rating, setRating] = useState<number | null>(null);
  const [hoverRating, setHoverRating] = useState<number | null>(null);

  const [groupName, fileName] = article.file.split('/');
  const contentReadyAt = useRef<number | null>(null);

  const {
    data: markdownContent,
    isLoading,
    isError
  } = useQuery(feedArticleOptions.file(groupName, fileName, true));

  useEffect(() => {
    if (!isLoading && !isError && markdownContent) {
      contentReadyAt.current = clock.now();
    }
  }, [isLoading, isError, markdownContent]);

  const captureReadingTime = useEffectEvent(() => {
    if (contentReadyAt.current !== null) {
      captureEvent('feed_article_reading_time', {
        file_name: fileName,
        group_name: groupName,
        reading_time_seconds: Math.round((clock.now() - contentReadyAt.current) / 1000)
      });
      contentReadyAt.current = null;
    }
  });

  useEffect(() => {
    return () => {
      captureReadingTime();
    };
  }, []);

  const submitRating = (value: number) => {
    setRating(value);
    captureEvent('monthly_recap_rated', {
      email: userEmail,
      rating: value,
      title: article.headline,
      month: article.month,
      region: article.region,
      date: article.date,
      read_time: article.readTime,
      timestamp: clock.date().toISOString()
    });
    enqueueSnackbar('Thanks for rating!', { variant: 'success' });
  };

  return (
    <>
      <Box sx={headerSx}>
        <IconButton
          aria-label='back'
          sx={iconButtonSx}
          onClick={onBack}
        >
          <ArrowBackIcon sx={{ fontSize: 16 }} />
        </IconButton>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 2, flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 20, whiteSpace: 'nowrap' }}>
            Monthly summaries
          </Typography>
          <Box
            sx={{
              backgroundColor: 'gx.highlight',
              px: '6px',
              height: 20,
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0
            }}
          >
            <Typography
              sx={{ fontSize: 10, fontWeight: 600, color: 'gx.text', letterSpacing: '0.2px' }}
            >
              NEW!
            </Typography>
          </Box>
        </Box>

        <IconButton
          aria-label='close'
          sx={iconButtonSx}
          onClick={onClose}
        >
          <CloseIcon sx={{ fontSize: 24 }} />
        </IconButton>
      </Box>

      <Box sx={{ overflowY: 'auto', flex: 1 }}>
        <Box
          sx={{
            mx: 'auto',
            width: 720,
            pt: 3,
            pb: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 500,
                color: 'gx.subtext',
                textTransform: 'uppercase'
              }}
            >
              {article.month}
            </Typography>
            <Dot />
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 500,
                color: 'gx.subtext',
                textTransform: 'uppercase'
              }}
            >
              {article.region}
            </Typography>
          </Box>

          <Typography
            sx={{
              fontSize: 32,
              fontWeight: 700,
              lineHeight: 'normal',
              color: 'gx.text',
              width: '100%'
            }}
          >
            {article.headline}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 500, color: 'gx.subtext' }}>
              Date: {article.date}
            </Typography>
            {article.readTime && (
              <>
                <Dot />
                <Typography sx={{ fontSize: 14, fontWeight: 500, color: 'gx.subtext' }}>
                  {article.readTime}
                </Typography>
              </>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            mx: 'auto',
            width: 728,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            pb: 4
          }}
        >
          {isLoading && <FeedArticleDialogSkeleton />}
          {isError && (
            <Typography
              color='error'
              sx={{ fontSize: 14 }}
            >
              Failed to load article content.
            </Typography>
          )}
          {markdownContent && (
            <Typography
              component='div'
              sx={{ overflowWrap: 'break-word', hyphens: 'auto', fontSize: '14px' }}
            >
              <Markdown>{markdownContent}</Markdown>
            </Typography>
          )}

          <Divider />

          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 2 }}
          >
            <Typography
              sx={{ fontSize: 20, fontWeight: 700, textAlign: 'center', color: 'gx.text' }}
            >
              Was this article valuable?
            </Typography>
            <Typography
              sx={{ fontSize: 14, fontWeight: 500, textAlign: 'center', color: 'gx.text' }}
            >
              Rate this month&apos;s recap to help us improve.
            </Typography>
            <Box
              sx={{ display: 'flex', gap: 0.5 }}
              onMouseLeave={() => {
                setHoverRating(null);
              }}
            >
              {[1, 2, 3, 4, 5].map(star => (
                <IconButton
                  disableRipple
                  key={star}
                  size='small'
                  sx={{ p: 0 }}
                  onClick={e => {
                    submitRating(getStarValue(e, star));
                  }}
                  onMouseMove={e => {
                    setHoverRating(getStarValue(e, star));
                  }}
                >
                  <StarDisplay
                    star={star}
                    value={hoverRating ?? rating ?? 0}
                  />
                </IconButton>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};
